import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { getPublicTenant } from '@/services/api';
import type { PublicTenant } from '@/types/api';
import { tenantManager } from '@/utils/tenantManager';

interface TenantContextValue {
  slug: string | null;
  tenant: PublicTenant | null;
  isLoading: boolean;
  setTenantSlug: (slug: string | null) => void;
  refreshTenant: () => void;
}

const TenantContext = createContext<TenantContextValue | undefined>(undefined);

const RESERVED_SUBDOMAINS = (import.meta.env.VITE_RESERVED_SUBDOMAINS ?? 'www,app')
  .split(',')
  .map((value: string) => value.trim().toLowerCase())
  .filter(Boolean);

const isIpAddress = (hostname: string) => /^\d{1,3}(\.\d{1,3}){3}$/.test(hostname);

const detectSlugFromHost = (hostname: string): string | null => {
  const lowerHost = hostname.toLowerCase();

  if (isIpAddress(lowerHost) || lowerHost === 'localhost') {
    return null;
  }

  const parts = lowerHost.split('.');
  if (parts.length < 2) {
    return null;
  }

  if (parts.length === 2) {
    return null;
  }

  const candidate = parts[0];
  if (!candidate || RESERVED_SUBDOMAINS.includes(candidate)) {
    return null;
  }

  return candidate;
};

const normalizeSlug = (value?: string | null) => {
  if (!value) {
    return null;
  }
  return value
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-{2,}/g, '-')
    .replace(/(^-+|-+$)/g, '') || null;
};

const extractSlugFromPath = (pathname: string) => {
  const match = pathname.match(/^\/t\/([a-z0-9-]{2,})(\/.*)?$/i);
  if (!match) {
    return { slug: null, cleanedPath: pathname };
  }

  const slug = normalizeSlug(match[1]);
  const rest = match[2] ?? '/';

  return {
    slug,
    cleanedPath: rest.startsWith('/') ? rest : `/${rest}`,
  };
};

const extractSlugFromQuery = (search: string) => {
  const params = new URLSearchParams(search);
  const slug = normalizeSlug(params.get('tenant'));
  return slug;
};

export const TenantProvider = ({ children }: { children: ReactNode }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [slug, setSlugState] = useState<string | null>(() => tenantManager.getSlug());

  const applySlug = useCallback(
    (nextSlug: string | null) => {
      setSlugState((previous) => {
        if (previous === nextSlug) {
          return previous;
        }
        tenantManager.setSlug(nextSlug);
        queryClient.removeQueries({ queryKey: ['tenant-meta'] });
        return nextSlug;
      });
    },
    [queryClient],
  );

  useEffect(() => {
    const hostSlug = detectSlugFromHost(window.location.hostname);
    const { slug: pathSlug, cleanedPath } = extractSlugFromPath(location.pathname);
    const querySlug = extractSlugFromQuery(location.search);

    const nextSlug = normalizeSlug(pathSlug ?? querySlug ?? hostSlug ?? tenantManager.getSlug());

    if (pathSlug && cleanedPath !== location.pathname) {
      const nextUrl = `${cleanedPath}${location.search}${location.hash}`;
      navigate(nextUrl === '' ? '/' : nextUrl, { replace: true });
    }

    applySlug(nextSlug);
  }, [location.pathname, location.search, location.hash, applySlug, navigate]);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['tenant-meta', slug],
    queryFn: async () => {
      try {
        const response = await getPublicTenant();
        return response ?? null;
      } catch (error) {
        console.error('Error fetching tenant:', error);
        // Se não há slug, retornar null (acesso direto via IP)
        if (!slug) {
          return null;
        }
        throw error;
      }
    },
    enabled: Boolean(slug),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const contextValue = useMemo<TenantContextValue>(
    () => ({
      slug,
      tenant: data ?? null,
      isLoading,
      setTenantSlug: (nextSlug) => applySlug(normalizeSlug(nextSlug)),
      refreshTenant: () => {
        void refetch();
      },
    }),
    [applySlug, data, isLoading, refetch, slug],
  );

  return <TenantContext.Provider value={contextValue}>{children}</TenantContext.Provider>;
};

export const useTenantContext = () => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenantContext must be used within TenantProvider');
  }
  return context;
};

